import { useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// value: [{contact_id?, name, email}] — existing contacts referenced by contact_id, new ones by name/email.
export const ProjectContacts = ({ client, value, onChange, testid = "pc" }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "" });
  const [q, setQ] = useState("");
  const [added, setAdded] = useState([]);
  const [saving, setSaving] = useState(false);
  const allContacts = [...(client?.contacts || []), ...added.filter((a) => !(client?.contacts || []).some((c) => c.id === a.id))];
  const existing = allContacts.filter((c) => !c.retired || value.some((v) => v.contact_id === c.id));
  const selectedIds = new Set(value.filter((c) => c.contact_id).map((c) => c.contact_id));
  const toggle = (c, on) => onChange(on ? [...value, { contact_id: c.id, name: c.name, email: c.email }] : value.filter((x) => x.contact_id !== c.id));
  const email = draft.email.trim().toLowerCase();
  const draftOk = draft.name.trim() && EMAIL.test(email);
  const addNew = async () => {
    if (!draftOk || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/contacts`, { name: draft.name.trim(), email });
      setAdded((a) => [...a, data]);
      if (!selectedIds.has(data.id)) onChange([...value, { contact_id: data.id, name: data.name, email: data.email }]);
      toast.success(data.existing ? `${data.name} already existed — selected` : `${data.name} added to ${client.company_name} and selected`);
      setDraft({ name: "", email: "" }); setAdding(false); setQ("");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (!client) return <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md p-3" data-testid={`${testid}-empty`}>Select a client company first to choose its contacts.</div>;
  const term = q.trim().toLowerCase();
  const shown = term ? existing.filter((c) => (c.name || "").toLowerCase().includes(term) || (c.email || "").toLowerCase().includes(term)) : existing;
  const selected = existing.filter((c) => selectedIds.has(c.id));
  return (
    <div className="border border-slate-200 rounded-md" data-testid={testid}>
      {existing.length > 5 && (
        <div className="relative border-b border-slate-100 p-2">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${existing.length} contacts by name or email…`} className="h-8 pl-8 text-xs" data-testid={`${testid}-search`} />
          {selected.length > 0 && <div className="text-[11px] text-slate-500 mt-1.5 px-1" data-testid={`${testid}-selected-summary`}>Selected: {selected.map((c) => c.name).join(", ")}</div>}
        </div>
      )}
      <div className="space-y-2 p-3 max-h-[220px] overflow-y-auto">
      {existing.length === 0 && value.length === 0 && <div className="text-xs text-slate-400">No contacts saved for {client.company_name} yet.</div>}
      {term && shown.length === 0 && <div className="text-xs text-slate-400" data-testid={`${testid}-no-match`}>No contacts match “{q}”.</div>}
      {shown.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`${testid}-existing-${c.id}`}>
          <Checkbox checked={selectedIds.has(c.id)} onCheckedChange={(v) => toggle(c, !!v)} data-testid={`${testid}-check-${c.id}`} />
          <span className="font-medium text-slate-800">{c.name}</span><span className="text-xs text-slate-500 truncate">— {c.email}</span>{added.some((a) => a.id === c.id) && <span className="text-[10px] uppercase text-teal-700">new</span>}
        </label>
      ))}
      {value.filter((c) => !c.contact_id).map((c, i) => (
        <div key={`new-${i}`} className="flex items-center gap-2 text-sm bg-teal-50 rounded px-2 py-1" data-testid={`${testid}-new-${i}`}>
          <span className="font-medium text-slate-800">{c.name}</span><span className="text-xs text-slate-500 truncate">— {c.email}</span><span className="text-[10px] uppercase text-teal-700">new</span>
          <button type="button" onClick={() => onChange(value.filter((x) => x !== c))} className="ml-auto text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-new-${i}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {adding ? (
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center pt-1">
          <Input placeholder="Contact name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-8 text-xs" data-testid={`${testid}-new-name`} autoFocus />
          <Input placeholder="Email address" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNew())} className="h-8 text-xs" data-testid={`${testid}-new-email`} />
          <div className="flex gap-1"><Button type="button" size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700" disabled={!draftOk || saving} onClick={addNew} data-testid={`${testid}-new-save`}>{saving ? "Adding…" : "Add & select"}</Button><Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setAdding(false)}>Cancel</Button></div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1 pt-1" data-testid={`${testid}-add-another`}><Plus className="h-3 w-3" /> Add contact <span className="text-slate-400 font-normal">(saved to {client.company_name} and selected)</span></button>
      )}
      </div>
    </div>
  );
};
